<script setup>
import { computed, watch } from 'vue'

const props = defineProps({
  games: {
    type: Array,
    default: () => []
  },
  projectedMinutes: {
    type: [Number, String],
    default: 0
  }
})

const emit = defineEmits(['stats-calculated'])

const calculatedStats = computed(() => {
  if (!props.games || props.games.length === 0) {
    return [
      { label: 'Points', projected: '0.00', per30: '0.00' },
      { label: 'Rebounds', projected: '0.00', per30: '0.00' },
      { label: 'Assists', projected: '0.00', per30: '0.00' },
      { label: '3PT', projected: '0.00', per30: '0.00' },
      { label: 'PRA', projected: '0.00', per30: '0.00' },
      { label: 'Turnovers', projected: '0.00', per30: '0.00' },
      { label: 'Blocks', projected: '0.00', per30: '0.00' },
      { label: 'Steals', projected: '0.00', per30: '0.00' }
    ]
  }

  // Calculate sums using the API response field names
  const totalMinutes = props.games.reduce((sum, game) => sum + Number(game.minutes || 0), 0)
  const totalPoints = props.games.reduce((sum, game) => sum + Number(game.points || 0), 0)
  const totalRebounds = props.games.reduce((sum, game) => sum + Number(game.total_rebounds || 0), 0)
  const totalAssists = props.games.reduce((sum, game) => sum + Number(game.assists || 0), 0)
  const totalThreePointers = props.games.reduce((sum, game) => sum + Number(game.three_point_field_goal || 0), 0)
  const totalSteals = props.games.reduce((sum, game) => sum + Number(game.steals || 0), 0)
  const totalBlocks = props.games.reduce((sum, game) => sum + Number(game.blocks || 0), 0)
  const totalTurnovers = props.games.reduce((sum, game) => sum + Number(game.turnovers || 0), 0)
  const totalPRA = props.games.reduce((sum, game) => sum + Number(game.pra || 0), 0)

  const projMinutes = Number(props.projectedMinutes || 0)

  // Calculate per 30 using formula: (sum of stat / sum of minutes) * 30
  const calculatePer30 = (totalStat) => {
    if (totalMinutes === 0) return '0.00'
    return ((totalStat / totalMinutes) * 30).toFixed(2)
  }

  // Calculate projection using formula: (per30 / 30) * projected_minutes
  const calculateProjection = (totalStat) => {
    if (totalMinutes === 0 || projMinutes === 0) return '0.00'
    const per30 = (totalStat / totalMinutes) * 30
    return ((per30 / 30) * projMinutes).toFixed(2)
  }

  return [
    { 
      label: 'Points', 
      projected: calculateProjection(totalPoints), 
      per30: calculatePer30(totalPoints),
      total: totalPoints,
      games: props.games.length
    },
    { 
      label: 'Rebounds', 
      projected: calculateProjection(totalRebounds), 
      per30: calculatePer30(totalRebounds),
      total: totalRebounds,
      games: props.games.length
    },
    { 
      label: 'Assists', 
      projected: calculateProjection(totalAssists), 
      per30: calculatePer30(totalAssists),
      total: totalAssists,
      games: props.games.length
    },
    { 
      label: '3PT', 
      projected: calculateProjection(totalThreePointers), 
      per30: calculatePer30(totalThreePointers),
      total: totalThreePointers,
      games: props.games.length
    },
    { 
      label: 'PRA', 
      projected: calculateProjection(totalPRA), 
      per30: calculatePer30(totalPRA),
      total: totalPRA,
      games: props.games.length
    },
    { 
      label: 'Turnovers', 
      projected: calculateProjection(totalTurnovers), 
      per30: calculatePer30(totalTurnovers),
      total: totalTurnovers,
      games: props.games.length
    },
    { 
      label: 'Blocks', 
      projected: calculateProjection(totalBlocks), 
      per30: calculatePer30(totalBlocks),
      total: totalBlocks,
      games: props.games.length
    },
    { 
      label: 'Steals', 
      projected: calculateProjection(totalSteals), 
      per30: calculatePer30(totalSteals),
      total: totalSteals,
      games: props.games.length
    }
  ]
})

// Create a summary object for easier consumption
const statsSummary = computed(() => {
  const summary = {
    gameCount: props.games.length,
    projectedMinutes: props.projectedMinutes,
    stats: {}
  }
  
  calculatedStats.value.forEach(stat => {
    summary.stats[stat.label.toLowerCase().replace(/\s+/g, '_')] = {
      total: stat.total,
      per30: parseFloat(stat.per30),
      projected: parseFloat(stat.projected),
      games: stat.games
    }
  })
  
  return summary
})

// Watch for changes and emit the calculated stats
watch(statsSummary, (newStats) => {
  emit('stats-calculated', newStats)
}, { deep: true, immediate: true })
</script>

<template>
  <div class="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-8 gap-4 w-full bg-gradient-to-br from-gray-50 to-gray-100 p-6 rounded-t-lg">
    <div v-for="stat in calculatedStats" :key="stat.label" 
         class="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200 p-4 flex flex-col items-center min-w-[120px] border border-gray-100">
      <div class="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">{{ stat.label }}</div>
      <div class="text-xl font-bold text-gray-800 mb-1">Proj: {{ stat.projected }}</div>
      <div class="text-sm text-gray-600 flex items-center gap-1">
        <span class="font-semibold text-pink-600">Per 30: {{ stat.per30 }}</span>
      </div>
      <div class="text-xs text-gray-400 mt-2 flex items-center gap-1">
        <span class="font-medium">{{ stat.total }}</span>
        <span class="text-gray-300">in</span>
        <span class="font-medium">{{ stat.games }}</span>
        <span class="text-gray-300">games</span>
      </div>
    </div>
  </div>
</template> 